import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'

import {
  CustomProfileConnectionFragmentDoc,
  CustomProfileEditAvatarQuery,
  CustomProfileEditAvatarQueryVariables,
  ItemType,
  Maybe,
  UpdateCustomProfile,
  UpdateCustomProfileMutation,
  UpdateCustomProfileMutationVariables,
  UserItemsAvatarConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { ItemProps } from '@/components/organisms'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter()

  const { data, networkStatus, fetchMore } = useQuery<
    CustomProfileEditAvatarQuery,
    CustomProfileEditAvatarQueryVariables
  >(QUERY_CUSTOM_PROFILE_AVATAR, {
    variables: { organizationPortfoliosType: ItemType.Avatar },
    notifyOnNetworkStatusChange: true,
  })

  const items: Maybe<ItemProps[]> =
    data?.user?.organizationPortfolios.edges.map(({ node }) => ({
      id: node.item.id,
      thumbnail: {
        uri: node?.item?.avatarResources?.avatarResource?.smallUri ?? '',
      },
    })) ?? []

  const [updateCustomProfile, { loading: customProfileUpdating }] = useMutation<
    UpdateCustomProfileMutation,
    UpdateCustomProfileMutationVariables
  >(UpdateCustomProfile)

  return {
    state: {
      loading: networkStatus === NetworkStatus.loading,
      fetching: networkStatus === NetworkStatus.fetchMore,
      customProfileUpdating,
      items: items,
      hasNextPage: !!data?.user?.organizationPortfolios?.pageInfo.hasNextPage,
      default: {
        id: data?.user?.customProfile?.avatarItem?.id ?? '',
        thumbnail: {
          uri:
            data?.user?.customProfile?.avatarItem?.avatarResources
              ?.avatarResource?.smallUri ?? '',
        },
      },
    },
    actions: {
      async onSaveCustomProfile(avatarItemId) {
        await updateCustomProfile({
          variables: { updateCustomProfileInputs: { avatarItemId } },
        })
        router.goBack(routerPages.MyProfile)
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            organizationPortfoliosType: ItemType.Avatar,
            pageInput: {
              cursor: data?.user?.organizationPortfolios?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY_CUSTOM_PROFILE_AVATAR = gql`
  query CustomProfileEditAvatar(
    $organizationPortfoliosType: ItemType
    $pageInput: PageInput
  ) {
    user {
      id
      organizationPortfolios(
        type: $organizationPortfoliosType
        pageInput: $pageInput
      ) {
        ...UserItemsAvatarConnection
      }
      customProfile {
        ...CustomProfileConnection
      }
    }
  }
  ${UserItemsAvatarConnectionFragmentDoc}
  ${CustomProfileConnectionFragmentDoc}
`
